import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, ExternalLink, Quote } from "lucide-react";

const clients = [
  {
    name: "Sisterfields",
    address: "3 Rue de l'Académie, Lausanne",
    description:
      "Café de spécialité et brunch, au cœur de la Cité de Lausanne.",
    quote:
      "Piktable a simplifié notre gestion quotidienne. Nos clients adorent la simplicité de réservation.",
    attribution: "L'équipe Sisterfields",
    image: "/images/sisterfields-interior.jpg",
    imageAlt:
      "Intérieur du restaurant Sisterfields à Lausanne - Client Piktable utilisant la gestion de réservations",
    bookingUrl: "https://piktable.ch/sisterfields-cafe",
  },
  {
    name: "Zao Cafe",
    address: "Rue de la Gare de Triage 6, 1020 Renens",
    description:
      "Café, brunch et restaurant à deux pas de la gare de Renens.",
    quote:
      "Nous sommes très satisfaits de notre expérience avec Piktable. L'équipe est particulièrement à l'écoute et toujours réactive, ce qui rend le service simple et agréable à utiliser au quotidien.",
    attribution: "L'équipe Zao Cafe",
    image: "/images/zao-cafe-interior.jpg",
    imageAlt: "Intérieur du Zao Cafe à Lausanne avec cerisier en fleurs - Client Piktable",
    bookingUrl: "https://piktable.ch/zao-cafe",
  },
  {
    name: "Le Comptoir de Valérie",
    address: "Rue de Couvaloup 15, 1110 Morges",
    description:
      "Crêperie bretonne artisanale, bio et agriculture raisonnée, dans le quartier piéton de Morges.",
    quote:
      "C'est un outil très simple à l'utilisation au quotidien. Il peut s'utiliser avec ou sans l'attribution des tables si on veut garder la main sur nos clients. L'équipe est très sympa et disponible.",
    attribution: "L'équipe Le Comptoir de Valérie",
    image: "/images/comptoir-valerie-interior.jpg",
    imageAlt:
      "Intérieur lumineux de la crêperie Le Comptoir de Valérie avec tables en bois et sets bleus - Client Piktable",
    bookingUrl: "https://piktable.ch/le-comptoir-de-valerie",
  },
  {
    name: "The Barista Lab",
    address: "Bvd de Saint-Georges 8, 1205 Genève",
    description:
      "Specialty coffee suisse, durable et éthique, né à Genève.",
    quote:
      "Facilite la vie de mes employés et plus besoin d'avoir de longs échanges via WhatsApp avec les clients pour booker une table et meilleures gestion du flux clientèle.",
    attribution: "L'équipe The Barista Lab",
    image: "/images/barista-lab-interior.jpg",
    imageAlt:
      "Terrasse animée du specialty coffee The Barista Lab à Genève - Client Piktable",
    bookingUrl: "https://piktable.ch/the-barista-lab",
  },
  {
    name: "POSTE|ROPRAZ",
    address: "Route de Bourg-Dessous 7, 1088 Ropraz",
    description:
      "Cuisine fraîche, locale et créative au Jorat — le fait maison comme évidence.",
    quote:
      "Piktable m'a immédiatement séduite par la fluidité de l'expérience côté client : la réservation est intuitive, claire, sans friction. Côté restaurant, la souplesse dans la gestion des créneaux et des disponibilités est réelle, on reprend enfin le contrôle de son planning et de sa base clients. Ce qui me convainc sur la durée, c'est la réactivité de l'équipe et une amélioration continue qui se ressent concrètement dans l'outil. Piktable évolue avec les besoins du terrain.",
    attribution: "Laurence, POSTE|ROPRAZ",
    image: "/images/poste-ropraz-interior.webp",
    imageAlt:
      "Salle intérieure du restaurant POSTE|ROPRAZ avec table dressée et tableaux aux murs - Client Piktable",
    bookingUrl: "https://piktable.ch/poste-ropraz",
  },
];

const reviewsSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Piktable",
  "url": "https://piktable.ch",
  "applicationCategory": "BusinessApplication",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": clients.length,
    "bestRating": "5",
    "worstRating": "1",
  },
  "review": clients.map((c) => ({
    "@type": "Review",
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": "5",
      "bestRating": "5",
    },
    "author": {
      "@type": "Organization",
      "name": c.name,
      "address": c.address,
    },
    "reviewBody": c.quote,
  })),
};

const ClientShowcase = () => {
  const rail = [...clients, ...clients];

  return (
    <section className="py-20 bg-card">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(reviewsSchema)}</script>
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-4">
            Quelques-unes de nos références
          </h2>
          <p className="text-xl text-muted-foreground">
            Un aperçu des restaurants qui utilisent Piktable au quotidien
          </p>
        </div>
      </div>

      {/* Marquee rail (full bleed) */}
      <div className="relative overflow-hidden">
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 z-10 bg-gradient-to-r from-card to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 z-10 bg-gradient-to-l from-card to-transparent" />

        <div className="flex gap-6 w-max animate-marquee py-4">
          {rail.map((client, idx) => (
            <article
              key={`${client.name}-${idx}`}
              className="bg-background rounded-lg overflow-hidden border border-border shadow-lg flex flex-col flex-shrink-0 w-[320px] md:w-[380px]"
              aria-hidden={idx >= clients.length ? "true" : undefined}
            >
              {/* Image */}
              {client.image ? (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={client.image}
                    alt={client.imageAlt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={500}
                    height={192}
                  />
                  <div className="absolute top-3 left-3">
                    <Badge variant="default" className="text-xs px-2 py-1">
                      Client Piktable
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="relative h-48 bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 flex items-center justify-center">
                  <span className="font-playfair text-3xl font-bold text-primary/40">
                    {client.name}
                  </span>
                </div>
              )}

              {/* Content */}
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-playfair text-xl font-bold text-foreground mb-1">
                  {client.name}
                </h3>
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <p className="text-xs">{client.address}</p>
                </div>
                <blockquote className="border-l-4 border-primary pl-3 italic text-foreground text-sm md:text-base font-medium leading-relaxed mb-3 flex-1">
                  <Quote className="w-5 h-5 mb-1 text-primary/60" />
                  <p className="line-clamp-6">{client.quote}</p>
                  <footer className="mt-2 text-xs font-semibold text-foreground not-italic">
                    {client.attribution}
                  </footer>
                </blockquote>

                <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">
                  {client.description}
                </p>

                <Button
                  size="sm"
                  className="w-full"
                  onClick={() =>
                    window.open(
                      client.bookingUrl,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  Réserver chez {client.name}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientShowcase;
